import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

function CoordinatorWiseSummaryModal(props) {
  const { open, onClose, data = [], loading = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Grand totals across all coordinators
  const grandTotals = React.useMemo(() => {
    return data.reduce(
      (acc, row) => {
        const t = row.totals || {};
        acc.order += parseFloat(t.order || 0);
        acc.issued += parseFloat(t.issued || 0);
        acc.invoiced += parseFloat(t.invoiced || 0);
        acc.discount += parseFloat(t.discount || 0);
        acc.paid += parseFloat(t.paid || 0);
        acc.balance += parseFloat(t.balance || 0);
        acc.dueByToday += parseFloat(t.dueByToday || 0);
        acc.balanceToPayByToday += parseFloat(t.balanceToPayByToday || 0);
        return acc;
      },
      {
        order: 0,
        issued: 0,
        invoiced: 0,
        discount: 0,
        paid: 0,
        balance: 0,
        dueByToday: 0,
        balanceToPayByToday: 0,
      }
    );
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '90vh',
          backgroundColor: '#f8fafc',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800} color="#0f172a">
            Coordinator Wise Summary
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Aggregated financial performance across {data.length} coordinators
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* EXECUTIVE KPI SUMMARY STRIP */}
      {data.length > 0 && (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #e2e8f0">
                <Typography variant="caption" color="#64748b" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL ORDERS
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#1e293b" noWrap>
                  {formatAmount(grandTotals.order)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #e2e8f0">
                <Typography variant="caption" color="#64748b" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL INVOICED
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#334155" noWrap>
                  {formatAmount(grandTotals.invoiced)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#f0fdf4" borderRadius={2} border="1px solid #bbf7d0">
                <Typography variant="caption" color="#16a34a" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL PAID
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#15803d" noWrap>
                  {formatAmount(grandTotals.paid)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box p={1} bgcolor="#fff7ed" borderRadius={2} border="1px solid #fed7aa">
                <Typography variant="caption" color="#ea580c" display="block" fontSize="0.68rem" fontWeight={700}>
                  TOTAL BALANCE
                </Typography>
                <Typography variant="body1" fontWeight={800} color="#c2410c" noWrap>
                  {formatAmount(grandTotals.balance)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress size={36} />
          </Box>
        ) : data.length > 0 ? (
          isMobile ? (
            // MOBILE RESPONSIVE CARDS
            data.map((row, index) => {
              const t = row.totals || {};
              return (
                <Paper
                  key={row.coordinator_id || index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2.5,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PersonOutlineIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                      {row.coordinator_name || 'No Coordinator'}
                    </Typography>
                  </Box>

                  <Box p={1} bgcolor="#f8fafc" borderRadius={2} border="1px solid #f1f5f9" mb={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                          Order Total
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#1e293b">
                          {formatAmount(t.order)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#64748b" fontSize="0.68rem" display="block">
                          Invoiced
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#334155">
                          {formatAmount(t.invoiced)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#16a34a" fontSize="0.68rem" display="block">
                          Paid
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#15803d">
                          {formatAmount(t.paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="#ea580c" fontSize="0.68rem" display="block">
                          Balance
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#c2410c">
                          {formatAmount(t.balance)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {(parseFloat(t.dueByToday || 0) > 0 || parseFloat(t.balanceToPayByToday || 0) > 0) && (
                    <Box p={0.8} bgcolor="#fff1f2" borderRadius={1.5} border="1px solid #fecdd3" display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="#e11d48" fontWeight={700}>
                        Due Today: {formatAmount(t.dueByToday)}
                      </Typography>
                      <Typography variant="caption" color="#be123c" fontWeight={700}>
                        To Pay: {formatAmount(t.balanceToPayByToday)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              );
            })
          ) : (
            // DESKTOP TABLE
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Coordinator</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Order Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Issued Invoice</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Invoiced</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Discount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Due Today</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>To Pay Today</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => {
                    const t = row.totals || {};
                    return (
                      <TableRow key={row.coordinator_id || index} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.coordinator_name || 'No Coordinator'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(t.order)}</TableCell>
                        <TableCell align="right">{formatAmount(t.issued)}</TableCell>
                        <TableCell align="right">{formatAmount(t.invoiced)}</TableCell>
                        <TableCell align="right">{formatAmount(t.discount)}</TableCell>
                        <TableCell align="right" sx={{ color: '#16a34a', fontWeight: 600 }}>{formatAmount(t.paid)}</TableCell>
                        <TableCell align="right" sx={{ color: '#ea580c', fontWeight: 600 }}>{formatAmount(t.balance)}</TableCell>
                        <TableCell align="right" sx={{ color: '#e11d48' }}>{formatAmount(t.dueByToday)}</TableCell>
                        <TableCell align="right" sx={{ color: '#be123c', fontWeight: 600 }}>{formatAmount(t.balanceToPayByToday)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                    <TableCell sx={{ fontWeight: 800 }}>Grand Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{formatAmount(grandTotals.order)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(grandTotals.issued)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(grandTotals.invoiced)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(grandTotals.discount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#16a34a' }}>{formatAmount(grandTotals.paid)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#ea580c' }}>{formatAmount(grandTotals.balance)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#e11d48' }}>{formatAmount(grandTotals.dueByToday)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#be123c' }}>{formatAmount(grandTotals.balanceToPayByToday)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography color="text.secondary">No coordinator summary data available</Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            bgcolor: '#0f172a',
            '&:hover': { bgcolor: '#1e293b' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CoordinatorWiseSummaryModal;
